<template>
  <div class="map-loader" role="status" aria-live="polite" aria-label="Loading map">
    <svg
      class="map-loader__svg"
      :viewBox="`0 0 ${VIEWBOX.width} ${VIEWBOX.height}`"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
    >
      <path class="map-loader__outline" :d="US_OUTLINE_PATH" pathLength="1" />
      <line
        v-for="(line, index) in GRID_LINES"
        :key="`grid-${index}`"
        class="map-loader__line"
        pathLength="1"
        :x1="line.x1"
        :y1="line.y1"
        :x2="line.x2"
        :y2="line.y2"
        :style="{ animationDelay: `${0.6 + index * 0.07}s` }"
      />
      <line
        v-for="(line, index) in FLOW_LINES"
        :key="`flow-${index}`"
        class="map-loader__flow"
        :x1="line.x1"
        :y1="line.y1"
        :x2="line.x2"
        :y2="line.y2"
        :style="{ animationDelay: `${index * 0.22}s` }"
      />
      <g>
        <template v-for="(node, index) in GRID_NODES" :key="`node-${index}`">
          <circle
            class="map-loader__halo"
            :cx="node.x"
            :cy="node.y"
            r="3"
            :style="{ animationDelay: `${index * 0.18}s` }"
          />
          <circle class="map-loader__node" :cx="node.x" :cy="node.y" r="3.5" />
        </template>
      </g>
    </svg>
    <p class="map-loader__caption">Mapping the grid<span>…</span></p>
  </div>
</template>

<script setup>
import { FLOW_LINES, GRID_LINES, GRID_NODES, US_OUTLINE_PATH, VIEWBOX } from './us_silhouette'
</script>

<style scoped>
.map-loader {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  overflow: hidden;
  background: radial-gradient(120% 120% at 50% 30%, #0b1f2a 0%, #06121a 72%);
}

.map-loader__svg {
  width: 78%;
  height: 78%;
  max-width: 880px;
  overflow: visible;
}

.map-loader__outline {
  fill: rgba(45, 212, 191, 0.06);
  stroke: #14b8a6;
  stroke-width: 2;
  stroke-linejoin: round;
  stroke-dasharray: 1;
  stroke-dashoffset: 1;
  animation: ml-draw 2s ease forwards;
}

.map-loader__line {
  fill: none;
  stroke: #0f766e;
  stroke-width: 1.4;
  stroke-linecap: round;
  stroke-dasharray: 1;
  stroke-dashoffset: 1;
  animation: ml-draw 1s ease forwards;
}

.map-loader__flow {
  fill: none;
  stroke: #5eead4;
  stroke-width: 2.2;
  stroke-linecap: round;
  stroke-dasharray: 14 220;
  stroke-dashoffset: 0;
  opacity: 0.9;
  animation: ml-flow 1.4s linear infinite;
}

.map-loader__node {
  fill: #5eead4;
}

.map-loader__halo {
  fill: none;
  stroke: #5eead4;
  stroke-width: 1.5;
  transform-box: fill-box;
  transform-origin: center;
  animation: ml-pulse 1.9s ease-out infinite;
}

.map-loader__caption {
  position: absolute;
  bottom: 28px;
  margin: 0;
  color: #5eead4;
  font: 600 13px/1 ui-sans-serif, system-ui, -apple-system, sans-serif;
  letter-spacing: 0.05em;
}

.map-loader__caption span {
  display: inline-block;
  animation: ml-blink 1.5s ease-in-out infinite;
}

@keyframes ml-draw {
  to { stroke-dashoffset: 0; }
}

/* -234 = one full dash period (14 + 220) for a seamless flow loop */
@keyframes ml-flow {
  to { stroke-dashoffset: -234; }
}

@keyframes ml-pulse {
  0% { transform: scale(1); opacity: 0.85; }
  100% { transform: scale(5); opacity: 0; }
}

@keyframes ml-blink {
  0%, 100% { opacity: 0.3; }
  50% { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .map-loader__outline,
  .map-loader__line {
    animation: none;
    stroke-dashoffset: 0;
  }

  .map-loader__flow {
    animation: none;
    opacity: 0;
  }

  .map-loader__halo,
  .map-loader__caption span {
    animation: none;
  }
}
</style>
